import AccessibilityZoneMap from './AccessibilityZoneMap';
import DataGrid from '@components/common/DataGrid';
import DeactivateProductAccessModal from './DeactivateProductAccessModal';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EmptyState from '@components/common/EmptyState';
import ErrorPage from '@components/common/ErrorPage';
import FeaturesManagerButton from '@components/common/FeaturesManagerButton';
import Header from '@components/common/layout/Header';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import LedgerTimeline from './LedgerTimeline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Notifier from '@components/common/Notifier';
import ProductAccessTermsSection from './ProductAccessTermsSection';
import PropTypes from 'prop-types';
import QuotaRow from '@components/common/quota/QuotaRow';
import QuotaUnitLabel from '@components/common/quota/QuotaUnitLabel';
import React, {useCallback, useMemo, useState} from 'react';
import RecallWorkspacesModal from './RecallWorkspacesModal';
import StatusCell from '@components/common/StatusCell';
import StatusChip from '@components/common/StatusChip';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import UndoIcon from '@mui/icons-material/Undo';
import routePath, {Routes} from '@account/utils/route';
import {ACCOUNT_MANAGER_URL} from '@account/config';
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {LineItemActivationStates} from '@account/constants/lineitems';
import {
  PA_BULK_LIMIT,
  deactivateProductAccess as _deactivateProductAccess,
  getProductAccesses,
  getWorkspacesForCA,
} from '@account/api';
import {ProductAccessStates} from '@account/constants/products';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {TODAY, displayDate, getUTCDate} from '@account/utils/dates';
import {
  dropRedundantTitleItem,
  parseDescription,
} from '@account/utils/product-description';
import {
  getActivationStateColor,
  getProductAccessStateColor,
  statusColorMappings,
} from '@account/utils/styles';
import {getError} from '@account/utils/errors';
import {
  getPrimaryQuota,
  normalizeSqmQuota,
  renderQuota,
} from '@account/utils/quota';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useProductAccess} from '@hooks/data/products/useProductAccess';
import {useProductAccessLedgerEntries} from '@hooks/data/products/useProductAccessLedgerEntries';
import {useProductAccessUsage} from '@hooks/data/products/useProductAccessUsage';

const DETAIL_LABEL_WIDTH = 148;

// A compact definition-list row: fixed-width label on the left, value on the
// right, so stacked detail fields align into a scannable column instead of
// drifting apart with uneven vertical gaps.
const DetailRow = ({label, tooltip, children}) => (
  <Box sx={{display: 'flex', gap: 2, alignItems: 'baseline'}}>
    <Box
      sx={{
        flex: `0 0 ${DETAIL_LABEL_WIDTH}px`,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="top">
          <InfoIcon color="disabled" fontSize="small" />
        </Tooltip>
      )}
    </Box>
    <Box sx={{minWidth: 0, flex: 1}}>{children}</Box>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  children: PropTypes.node,
};

const DESCRIPTION_LINE_CLAMP = 2;

const DESCRIPTION_VISIBLE_ITEMS = 3;

const DescriptionText = ({description, title}) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = React.useRef(null);

  const {mode, items} = React.useMemo(
    () => parseDescription(description),
    [description]
  );

  React.useEffect(() => {
    const el = ref.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight);
  }, [description]);

  if (mode === 'list') {
    const listItems = dropRedundantTitleItem(items, title);
    const visible = expanded
      ? listItems
      : listItems.slice(0, DESCRIPTION_VISIBLE_ITEMS);
    const hiddenCount = listItems.length - DESCRIPTION_VISIBLE_ITEMS;

    return (
      <Box sx={{mb: 3}}>
        <Stack spacing={0.5}>
          {visible.map(item => (
            <Typography
              key={item.key ?? item.value}
              variant="body2"
              color="text.secondary"
            >
              {item.key && (
                <Box
                  component="span"
                  sx={{color: 'text.primary', fontWeight: 500}}
                >
                  {item.key}:{' '}
                </Box>
              )}
              {item.value}
            </Typography>
          ))}
        </Stack>
        {hiddenCount > 0 && (
          <Link
            component="button"
            variant="body2"
            onClick={() => setExpanded(e => !e)}
            sx={{mt: 0.5}}
          >
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          </Link>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{mb: 3}}>
      <Typography
        ref={ref}
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : DESCRIPTION_LINE_CLAMP,
          overflow: 'hidden',
        }}
      >
        {description}
      </Typography>
      {(clamped || expanded) && (
        <Link
          component="button"
          variant="body2"
          onClick={() => setExpanded(e => !e)}
          sx={{mt: 0.5}}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Box>
  );
};

DescriptionText.propTypes = {
  description: PropTypes.string.isRequired,
  title: PropTypes.string,
};

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={TravelExploreIcon}
    text="No line items"
    textSx={{maxWidth: '300px'}}
  />
);

const OPERATION_LABELS = {
  PROVISION: 'Provisioned',
  ALLOCATE: 'Allocated',
  RECALL: 'Recalled',
  ADJUSTMENT: 'Adjustment',
  CANCELLATION: 'Cancellation',
  REVERSAL: 'Reversal',
  EXPIRED: 'Expired',
};

const NewProductDetailPage = () => {
  const auth = useAuth();
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  // Planeteers can view a product; the mutating actions below stay admin-only.
  const canViewProduct = hasRoleLevel(auth, [
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);
  const {
    showCustomerAccounts,
    showAllAccounts,
    sqkmUsedConversionWorkaround,
    showLedgerHistory,
  } = useFlags();
  const {productAccessId} = useParams();
  const queryClient = useQueryClient();

  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isRecallOpen, setIsRecallOpen] = useState(false);
  const [kebabAnchorEl, setKebabAnchorEl] = useState(null);
  const [showAllTerms, setShowAllTerms] = useState(false);
  const [historyView, setHistoryView] = useState('table');
  const [isMapWide, setIsMapWide] = useState(false);

  const {data, isLoading, error} = useProductAccess(productAccessId, {
    enabled: !!productAccessId && showCustomerAccounts,
  });

  const productAccess = data ?? {};

  // A workspace PA is a ProductAccess with workspace_id set (CA-level PAs have
  // it null). This page is shared between the two; workspace context swaps a few
  // CA-only fields/actions for workspace-appropriate ones.
  const isWorkspacePa = !!productAccess.workspace_id;

  // Active-term view scopes the grouped ledger to the current term's validity
  // window on the server (CGO-2801 overlap filter: valid_to > from AND
  // valid_from < to); "All terms" omits the window.
  const ledgerWindow =
    !showAllTerms &&
    productAccess.term_start_date &&
    productAccess.term_end_date
      ? {
          validFrom: productAccess.term_start_date,
          validTo: productAccess.term_end_date,
        }
      : {};

  const {
    data: ledgerData,
    isLoading: ledgerLoading,
    isError: ledgerError,
  } = useProductAccessLedgerEntries(
    productAccessId,
    {limit: 500, offset: 0, ...ledgerWindow},
    {
      enabled: !!productAccessId && showCustomerAccounts && showLedgerHistory,
      keepPreviousData: true,
      retry: false,
    }
  );

  // Ledger history is a newer endpoint that may not be deployed in every
  // environment (and isn't populated for every PA). When the fetch errors
  // (e.g. 404 where the endpoint isn't available yet), fall back to the
  // line-items history view rather than showing an empty/misleading ledger.
  const ledgerAvailable = showLedgerHistory && !ledgerError;

  const hasCoverageMap =
    Array.isArray(productAccess.accessibility_zone) &&
    productAccess.accessibility_zone.length > 0;

  // In the compact two-column layout, stack the detail fields into a single
  // column (they sit beside the map); otherwise keep the 2-up grid.
  const isTwoColumn = hasCoverageMap && !isMapWide;
  const fieldMd = isTwoColumn ? 12 : 6;

  const today = new Date().toISOString();
  const today10 = today.slice(0, 10);

  // The server has already scoped to the selected window; these are the real ops.
  const serverLedgerRows = ledgerData?.results ?? [];

  // Only CA-level rows move the unallocated CA pool: exclude workspace-level
  // provisions (destination set); allocations already carry a signed amount.
  const affectsCaPool = r =>
    !(r.operation_type === 'PROVISION' && r.destination_workspace_id);

  // Natural term expiry writes no ledger entry (only term shortening does), so a
  // lapsed term's unallocated remainder would silently vanish and the timeline
  // wouldn't add up. Derive a per-validity-window EXPIRED debit: net each window's
  // CA-pool rows, and when its valid_to has passed, emit a debit for the remainder
  // so the visible running total lands on the authoritative "remaining" below.
  // Derived annotation only (not a ledger event) - flagged for review (CGO-3170).
  const derivedExpiredRows = (() => {
    const byWindow = new Map();
    for (const r of serverLedgerRows) {
      if (!affectsCaPool(r)) continue;
      const amt = Number(r.amount);
      if (!Number.isFinite(amt)) continue;
      const key = `${r.valid_from ?? ''}|${r.valid_to ?? ''}`;
      const w = byWindow.get(key) ?? {
        net: 0,
        valid_from: r.valid_from,
        valid_to: r.valid_to,
        quota_units: r.quota_units,
      };
      w.net += amt;
      byWindow.set(key, w);
    }
    const rows = [];
    for (const [key, w] of byWindow) {
      const lapsed = w.valid_to && w.valid_to.slice(0, 10) < today10;
      if (lapsed && w.net > 0) {
        rows.push({
          operation_id: `expired-${key}`,
          operation_type: 'EXPIRED',
          amount: String(-w.net),
          quota_units: w.quota_units,
          valid_from: w.valid_from,
          valid_to: w.valid_to,
          created_at: w.valid_to,
          destination_workspace_id: null,
          destination_workspace_name: null,
          source_line_item_id: null,
          created_by: null,
        });
      }
    }
    return rows;
  })();

  const groupedLedgerRows = [...serverLedgerRows, ...derivedExpiredRows].sort(
    (a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '')
  );

  // Authoritative remaining unallocated quota (display units), same source as
  // AllocationsPage. The ledger timeline explains this number; this is the truth.
  const remainingUnallocated =
    productAccess.unlimited_quota ||
    productAccess.quota_total === null ||
    productAccess.quota_total === undefined
      ? null
      : Math.max(
          0,
          productAccess.quota_total - (productAccess.quota_used ?? 0)
        );

  const {organization_id: orgId, customer_account_id: customerAccountId} =
    productAccess;

  const {
    isLoading: usageLoading,
    error: usageError,
    data: usageData,
  } = useProductAccessUsage(productAccessId, {
    enabled: !!productAccessId && showCustomerAccounts,
  });

  const {data: workspacePasData, isLoading: workspacePasLoading} = useQuery(
    ['workspacePas', customerAccountId, productAccess.edp_id],
    () =>
      getProductAccesses({
        customerAccountId,
        search: productAccess.edp_id,
        limit: PA_BULK_LIMIT,
      }),
    {
      enabled:
        isPlanetAdmin &&
        !!customerAccountId &&
        !!productAccess.edp_id &&
        isRecallOpen,
    }
  );
  const workspacePas = (workspacePasData?.results ?? []).filter(
    pa => !!pa.workspace_id
  );
  const workspacePasTruncated =
    (workspacePasData?.meta?.count ?? 0) > PA_BULK_LIMIT;

  // The PA detail response carries workspace_id but not a name. For a workspace
  // PA, resolve the display name from IMS (same source the Workspaces tab uses),
  // falling back to a truncated id.
  const {data: workspacesForCA} = useQuery(
    // Same key WorkspaceDetailPage uses, so the resolved list hydrates from
    // cache when navigating between the two pages instead of refetching.
    ['workspaces-for-ca', customerAccountId],
    () => getWorkspacesForCA(customerAccountId),
    {
      enabled: isWorkspacePa && !!customerAccountId,
      staleTime: 5 * 60 * 1000,
    }
  );
  const workspaceName =
    workspacesForCA?.workspaces?.find(
      w => String(w.workspace_id) === String(productAccess.workspace_id)
    )?.name ??
    (productAccess.workspace_id
      ? `${String(productAccess.workspace_id).slice(0, 8)}…`
      : null);

  const handleDeactivateModalClose = () => {
    setIsDeactivateModalOpen(false);
  };

  const {
    mutate: deactivateProductAccess,
    isLoading: isLoadingDeactivationResponse,
    isError: isErrorDeactivating,
    error: errorDeactivation,
  } = useMutation(_deactivateProductAccess, {
    onSuccess: async () => {
      handleDeactivateModalClose();

      await queryClient.invalidateQueries(['productAccess']);
    },
  });

  const handleRemoveProduct = () => {
    deactivateProductAccess(Number(productAccessId));

    handleDeactivateModalClose();
  };

  const productTitle = productAccess?.title;

  const breadcrumbs = useMemo(
    () =>
      // A workspace PA carries an organization_id, so without this branch it
      // would fall into the org trail below and the CA crumb would link to the
      // org page. Give it the CA -> workspace -> product trail instead (matches
      // WorkspaceDetailPage). Gated on showAllAccounts because the ACCOUNT_DETAIL
      // and WORKSPACE_DETAIL routes are only registered under that flag.
      isWorkspacePa && showAllAccounts && customerAccountId
        ? [
            {label: 'All Accounts', url: Routes.ACCOUNTS},
            {
              label: productAccess?.customer_account_name ?? '...',
              url: routePath(Routes.ACCOUNT_DETAIL, {
                accountId: customerAccountId,
              }),
            },
            {
              label: workspaceName ?? '...',
              url: routePath(Routes.WORKSPACE_DETAIL, {
                accountId: customerAccountId,
                workspaceId: productAccess?.workspace_id,
              }),
            },
            {label: productTitle},
          ]
        : orgId
          ? [
              {label: 'All Organizations', url: Routes.ORGS},
              {
                label: productAccess?.organization_name ?? '...',
                url: routePath(Routes.ORG_DETAIL, {orgId: orgId}),
              },
              {label: productTitle},
            ]
          : customerAccountId
            ? [
                ...(showAllAccounts
                  ? [
                      {label: 'All Accounts', url: Routes.ACCOUNTS},
                      {
                        label: productAccess?.customer_account_name ?? '...',
                        url: routePath(Routes.ACCOUNT_DETAIL, {
                          accountId: customerAccountId,
                        }),
                      },
                    ]
                  : []),
                {label: productTitle},
              ]
            : [
                {label: 'All Organizations', url: Routes.ORGS},
                {label: productTitle},
              ],
    [
      customerAccountId,
      isWorkspacePa,
      orgId,
      productAccess?.customer_account_name,
      productAccess?.organization_name,
      productAccess?.workspace_id,
      productTitle,
      showAllAccounts,
      workspaceName,
    ]
  );

  const getDaysUntilStartDateText = useCallback(startDate => {
    const numDays = getUTCDate(startDate).startOf('day').diff(TODAY).as('days');

    return `Customer access starts in ${numDays} ${
      numDays === 1 ? 'day' : 'days'
    }`;
  }, []);

  const ledgerColumns = useMemo(() => {
    // Link to the in-app workspace details page when we have CA context; fall
    // back to Account Manager otherwise (e.g. the org-scoped view). Shared by
    // the Source (recall) and Destination (allocate) columns.
    const renderWorkspaceLink = (workspaceId, workspaceName) => {
      const label = workspaceName ?? workspaceId;
      return customerAccountId ? (
        <Link
          component={RouterLink}
          to={routePath(Routes.WORKSPACE_DETAIL, {
            accountId: customerAccountId,
            workspaceId,
          })}
          variant="body2"
          underline="hover"
        >
          {label}
        </Link>
      ) : (
        <Link
          href={`https://insights.planet.com/account/#/workspaces/${encodeURIComponent(
            workspaceId
          )}/info`}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          underline="hover"
        >
          {label}
        </Link>
      );
    };
    return [
      {
        field: 'created_at',
        headerName: 'Date',
        flex: 0.22,
        renderCell: params => (
          <Typography variant="body2">
            {displayDate(params.row.created_at)}
          </Typography>
        ),
      },

      {
        field: 'amount',
        headerName: 'Amount',
        renderCell: params => {
          const {operation_type, amount, quota_units} = params.row;
          if (amount === null || amount === undefined) {
            return (
              <Typography color="text.secondary" variant="body2">
                —
              </Typography>
            );
          }
          const isProvision = operation_type === 'PROVISION';
          const isAllocate = operation_type === 'ALLOCATE';
          const isRecall = operation_type === 'RECALL';
          const isExpired = operation_type === 'EXPIRED';
          const numAmount = Number(amount);
          const positive = numAmount > 0;
          if (isAllocate) {
            return (
              <Typography
                variant="body2"
                sx={{
                  color: positive ? 'text.primary' : 'error.main',
                  fontWeight: positive ? 500 : 400,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {positive ? '+' : '−'}
                {renderQuota(Math.abs(numAmount), quota_units)}
              </Typography>
            );
          }
          if (isRecall) {
            // Quota returning from a workspace to this account — show as an
            // incoming (green, signed +) movement, the mirror of an allocate.
            return (
              <Typography
                variant="body2"
                sx={{
                  color: 'success.main',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                +{renderQuota(Math.abs(numAmount), quota_units)}
              </Typography>
            );
          }
          if (isExpired) {
            return (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.disabled',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                −{renderQuota(Math.abs(numAmount), quota_units)}
              </Typography>
            );
          }
          const color = isProvision || positive ? 'success.main' : 'error.main';
          return (
            <Typography color={color} variant="body2">
              {isProvision && '+'}
              {!isProvision && !positive && '-'}
              {renderQuota(Math.abs(numAmount), quota_units)}
            </Typography>
          );
        },
        flex: 0.16,
      },
      {
        field: 'operation_type',
        headerName: 'Type',
        renderCell: params => {
          if (params.value === 'EXPIRED')
            return (
              <Typography variant="body2" color="text.disabled">
                Expired
              </Typography>
            );
          return (
            <StatusCell
              status={OPERATION_LABELS[params.value] ?? params.value}
              sx={theme => ({
                color: getProductAccessStateColor(theme, params.value),
                fontWeight: 'inherit',
              })}
            />
          );
        },
        flex: 0.14,
      },
      {
        field: 'source_line_item_id',
        headerName: 'Source',
        renderCell: params => {
          const {
            operation_type,
            source_line_item_id,
            source_pl_number,
            source_workspace_id,
            source_workspace_name,
          } = params.row;
          if (operation_type === 'PROVISION') {
            // Provisions trace back to the order that funded them - show the
            // Opportunity ID (backend source_pl_number) and keep the raw
            // line-item id in a tooltip. The PL-xxxx format is self-evident in
            // this column, so no "Opportunity" prefix. Manual/goodwill
            // provisions carry a line item but no Opportunity, so fall back to
            // the line item alone.
            if (source_pl_number) {
              return (
                <Tooltip
                  title={
                    source_line_item_id
                      ? `Line Item ${source_line_item_id}`
                      : ''
                  }
                >
                  <Typography variant="body2">{source_pl_number}</Typography>
                </Tooltip>
              );
            }
            if (source_line_item_id) {
              return (
                <Typography variant="body2">
                  Line Item {source_line_item_id}
                </Typography>
              );
            }
          }
          // Recall: quota came FROM a workspace back to this account. We always
          // know it originated in a workspace, so when the id is missing show an
          // explicit "Unknown workspace" rather than falling through to a bare
          // dash (keeps the column consistent with ALLOCATE/EXPIRED).
          if (operation_type === 'RECALL') {
            return source_workspace_id ? (
              renderWorkspaceLink(source_workspace_id, source_workspace_name)
            ) : (
              <Typography color="text.secondary" variant="body2">
                Unknown workspace
              </Typography>
            );
          }
          if (operation_type === 'ALLOCATE' || operation_type === 'EXPIRED') {
            return (
              <Typography color="text.secondary" variant="body2">
                This account
              </Typography>
            );
          }
          return (
            <Typography color="text.secondary" variant="body2">
              —
            </Typography>
          );
        },
        flex: 0.16,
      },
      {
        field: 'destination_workspace_id',
        headerName: 'Destination',
        renderCell: params => {
          const {
            operation_type,
            destination_workspace_name,
            destination_workspace_id,
          } = params.row;
          if (operation_type === 'ALLOCATE' && destination_workspace_id) {
            return renderWorkspaceLink(
              destination_workspace_id,
              destination_workspace_name
            );
          }
          // Provision lands in this account; recall returns quota to it.
          if (operation_type === 'PROVISION' || operation_type === 'RECALL') {
            return (
              <Typography color="text.secondary" variant="body2">
                This account
              </Typography>
            );
          }
          return (
            <Typography color="text.secondary" variant="body2">
              —
            </Typography>
          );
        },
        flex: 0.18,
      },
      {
        field: 'created_by',
        headerName: 'By',
        valueGetter: params => {
          const v = params.row.created_by;
          if (!v) return 'System';
          if (typeof v === 'string') return v;
          if (v.type === 'system') return 'System';
          if (v.type === 'hidden') return 'Hidden';
          return v.name ?? 'System';
        },
        flex: 0.1,
      },
    ];
  }, [customerAccountId]);

  const columns = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: 'Transaction Date',
        valueGetter: params => displayDate(params.value),
        flex: 0.2,
      },
      {
        field: 'quota_limit',
        headerName: 'Quota Limit',
        valueGetter: params => {
          if (params.row.has_unlimited_quota) return 'Unlimited';
          return renderQuota(params.row.quota_limit, params.row.quota_units);
        },
        renderCell: params => {
          return params.value === 'Error' ? (
            <Typography color="error" variant="body2">
              Error
            </Typography>
          ) : (
            params.value
          );
        },
        flex: 0.2,
      },
      {
        field: 'start_date',
        headerName: 'Access Dates',
        valueGetter: params =>
          `${displayDate(params.row.start_date)} to ${displayDate(
            params.row.end_date
          )}`,
        flex: 0.4,
      },
      {
        field: 'state',
        headerName: 'Status',
        valueGetter: params => params.row.activation_state,
        renderCell: params => (
          <StatusCell
            status={params.value}
            sx={theme => ({
              color: getActivationStateColor(theme, params.value),
              fontWeight: 'inherit',
            })}
            tooltipText={
              params.value === LineItemActivationStates.PENDING
                ? getDaysUntilStartDateText(params.row.start_date)
                : null
            }
          />
        ),
        flex: 0.2,
      },
    ],
    [getDaysUntilStartDateText]
  );

  if (!showCustomerAccounts || !canViewProduct) {
    return <ErrorPage statusCode={404} />;
  }

  if (error) {
    return <ErrorPage {...getError(error)} />;
  }

  if (isLoading) {
    return <Notifier progress open message="Loading product..." />;
  }

  if (!data) {
    return null;
  }

  const productIsActive = productAccess.state === ProductAccessStates.ACTIVE;

  const canDeactivateProduct = isPlanetAdmin && productIsActive;

  const productAccessHasTileUnits = productAccess.quota_units === 'tile';
  const canRecall =
    isPlanetAdmin &&
    !!customerAccountId &&
    !productAccessHasTileUnits &&
    !isWorkspacePa;
  const supportsReservations = (productAccess.quota_style ?? '').startsWith(
    'billableaoi'
  );

  const showReserveQuotaButton =
    productIsActive && !productAccessHasTileUnits && supportsReservations;

  const showQuotaBar =
    productIsActive &&
    !usageError &&
    !usageLoading &&
    !productAccessHasTileUnits;

  const showDataAvailability =
    !!productAccess.toa_start_date || !!productAccess.toa_end_date;

  // Processing-units note for tile products. Rendered inside the left detail
  // column when the coverage map is present (fills the space beside the map),
  // otherwise full-width below the details.
  const processingUnitsCallout = productAccessHasTileUnits ? (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: 'grey.100',
        borderColor: 'grey.300',
      }}
    >
      <CardContent>
        <Typography variant="subtitle1">
          This product consumes{' '}
          <Link
            color="primary"
            href="https://docs.planet.com/platform/processing-units"
            target="_blank"
            rel="noreferer noopener"
          >
            processing units
          </Link>{' '}
          when you view basemap tiles. Please refer to your Processing Units{' '}
          <Link
            color="primary"
            href={`${ACCOUNT_MANAGER_URL}#/usage`}
            target="_blank"
            rel="noreferer noopener"
          >
            usage
          </Link>{' '}
          to track consumption and understand how much has been used.
        </Typography>
      </CardContent>
    </Card>
  ) : null;

  return (
    <Box sx={{paddingBottom: 8}}>
      <Header
        name={
          <>
            <span>{productTitle}</span>{' '}
            {productAccess.state === ProductAccessStates.DEACTIVATED && (
              <StatusChip
                label="Deactivated"
                statusColor={statusColorMappings.deactivated}
                style={{
                  marginLeft: 6,
                }}
              />
            )}
          </>
        }
        breadcrumbs={breadcrumbs}
        helmetTitle={productTitle}
      >
        <Stack gap={1} alignItems="center" flexDirection="row">
          {showReserveQuotaButton && (
            <FeaturesManagerButton
              orgId={orgId}
              showReserveQuotaCopy={hasRoleLevel(auth, [
                roleLevels.planet_user,
                roleLevels.planet_admin,
              ])}
            />
          )}

          {(canDeactivateProduct || canRecall) && (
            <>
              <IconButton
                aria-label="More options"
                size="small"
                onClick={e => setKebabAnchorEl(e.currentTarget)}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={kebabAnchorEl}
                open={!!kebabAnchorEl}
                onClose={() => setKebabAnchorEl(null)}
              >
                {canRecall && (
                  <MenuItem
                    onClick={() => {
                      setKebabAnchorEl(null);
                      setIsRecallOpen(true);
                    }}
                  >
                    <UndoIcon sx={{mr: 1, fontSize: '1.1rem'}} />
                    Recall quota from workspaces
                  </MenuItem>
                )}
                {canRecall && canDeactivateProduct && <Divider />}
                {canDeactivateProduct && (
                  <MenuItem
                    sx={theme => ({color: theme.palette.error.main})}
                    onClick={() => {
                      setKebabAnchorEl(null);
                      setIsDeactivateModalOpen(true);
                    }}
                  >
                    <DeleteIcon sx={{mr: 1}} />
                    Deactivate product
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Stack>
      </Header>

      {productAccess.description && (
        <DescriptionText
          description={productAccess.description}
          title={productTitle}
        />
      )}

      <Grid container spacing={3} alignItems="stretch">
        <Grid
          xs={12}
          md={hasCoverageMap && !isMapWide ? 7 : 12}
          sx={{display: 'flex', flexDirection: 'column'}}
        >
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid xs={12} md={fieldMd}>
              <DetailRow
                label="Status"
                tooltip="Defined by provisioning completeness and access dates. Pending status has future access dates."
              >
                <StatusCell
                  status={productAccess.state}
                  variant="body1"
                  sx={theme => ({
                    color: getProductAccessStateColor(
                      theme,
                      productAccess.state
                    ),
                    fontWeight: 'inherit',
                  })}
                />
              </DetailRow>
            </Grid>

            {productAccess.pl_numbers &&
              productAccess.pl_numbers.length > 0 && (
                <Grid xs={12} md={fieldMd}>
                  <DetailRow
                    label={`PL ${
                      productAccess.pl_numbers.length === 1
                        ? 'Number'
                        : 'Numbers'
                    }`}
                  >
                    <Typography variant="body1">
                      {(() => {
                        const plNumbers = productAccess.pl_numbers;

                        if (plNumbers.length <= 3) {
                          return plNumbers.join(', ');
                        }

                        // Show first 3 with tooltip for full list
                        const displayText = `${plNumbers.slice(0, 3).join(', ')}, ...`;
                        const tooltipText = plNumbers.join(', ');

                        return (
                          <Tooltip title={tooltipText} placement="top">
                            <span>{displayText}</span>
                          </Tooltip>
                        );
                      })()}
                    </Typography>
                  </DetailRow>
                </Grid>
              )}

            <Grid xs={12} md={fieldMd}>
              <DetailRow
                label="Access Dates"
                tooltip="Time period in which user can access data."
              >
                <Typography variant="body1">
                  {displayDate(productAccess.term_start_date)} to{' '}
                  {displayDate(productAccess.term_end_date)}
                </Typography>
              </DetailRow>
            </Grid>

            {showDataAvailability && (
              <Grid xs={12} md={fieldMd}>
                <DetailRow
                  label="Data Availability"
                  tooltip="Time extent for all available data"
                >
                  <Typography variant="body1">
                    {productAccess.toa_start_date
                      ? displayDate(productAccess.toa_start_date)
                      : 'Start of term'}{' '}
                    to{' '}
                    {productAccess.toa_end_date
                      ? displayDate(productAccess.toa_end_date)
                      : 'end of term'}
                  </Typography>
                </DetailRow>
              </Grid>
            )}

            {isWorkspacePa ? (
              <Grid xs={12} md={fieldMd}>
                <DetailRow label="Workspace">
                  <Typography variant="body1">
                    {workspaceName ? (
                      <Link
                        color="primary.dark"
                        href={`https://insights.planet.com/account/#/workspaces/${productAccess.workspace_id}/info`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {workspaceName}
                      </Link>
                    ) : (
                      '...'
                    )}
                  </Typography>
                </DetailRow>
              </Grid>
            ) : (
              isPlanetAdmin &&
              customerAccountId && (
                <Grid xs={12} md={fieldMd}>
                  <DetailRow label="Customer Account">
                    <Typography variant="body1">
                      {showAllAccounts ? (
                        <Link
                          color="primary.dark"
                          component={RouterLink}
                          to={routePath(Routes.ACCOUNT_DETAIL, {
                            accountId: customerAccountId,
                          })}
                          underline="hover"
                        >
                          {productAccess.customer_account_name ?? '...'}
                        </Link>
                      ) : (
                        (productAccess.customer_account_name ?? '...')
                      )}
                    </Typography>
                  </DetailRow>
                </Grid>
              )
            )}
          </Grid>
          {isTwoColumn && processingUnitsCallout && (
            <Box sx={{marginTop: 'auto', paddingTop: 3}}>
              {processingUnitsCallout}
            </Box>
          )}
        </Grid>

        {hasCoverageMap && (
          <Grid xs={12} md={isMapWide ? 12 : 5}>
            <AccessibilityZoneMap
              azRefs={productAccess.accessibility_zone}
              productAccessId={productAccessId}
              enableReservedAois={isWorkspacePa && supportsReservations}
              isWide={isMapWide}
              onToggleWide={() => setIsMapWide(prev => !prev)}
            />
          </Grid>
        )}
      </Grid>

      {showQuotaBar && (
        <Stack sx={{gap: 1, marginTop: 3}}>
          <Typography variant="subtitle1" sx={{fontWeight: 700}}>
            {isWorkspacePa ? 'Workspace Quota' : 'Unallocated Quota'}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            Quota unit:{' '}
            <QuotaUnitLabel
              unit={
                usageData?.non_recurring?.[0]?.quota_unit ??
                productAccess.quota_units
              }
              variant="body2"
            />
          </Typography>
          {usageData?.non_recurring?.map(quota => {
            const {used, limit} = normalizeSqmQuota(
              {
                current: quota.used,
                limit: quota.quota_limit,
                units: quota.quota_unit,
              },
              sqkmUsedConversionWorkaround
            );
            return (
              <QuotaRow
                key={`${quota.quota_unit}-${quota.quota_limit}-${quota.start_date}`}
                quota={{limit, current: used, enabled: true}}
                quotaUnits={quota.quota_unit}
                subscriptionEnd={quota.end_date}
              />
            );
          })}
          {/**
            if there is no usage data fall back to the PA info, this will apply to reservation products
            this is fragile and should depend on `supports_reservations` but the API is not returning this bit.

            productAccess.unlimited_quota is an ANY-dimension rollup — it's true if *any*
            quota policy on the variant is unlimited (e.g. a genuinely unlimited `tile`
            streaming dimension), which can mask a finite primary/reservable quota (e.g.
            sqkm for reserved_area products like open water). getPrimaryQuota reads the
            per-dimension `quotas` array instead so only the primary dimension's own
            unlimited flag is used.
           */}
          {!usageData?.non_recurring?.length && (
            <QuotaRow
              quota={{
                ...getPrimaryQuota(productAccess),
                enabled: true,
              }}
              quotaUnits={productAccess.quota_units}
              subscriptionEnd={productAccess.term_end_date}
            />
          )}
        </Stack>
      )}

      {!isTwoColumn && processingUnitsCallout && (
        <Grid sm={12} xl={12} sx={{marginTop: 3}}>
          {processingUnitsCallout}
        </Grid>
      )}

      {isPlanetAdmin && (
        <ProductAccessTermsSection
          productAccessId={productAccessId}
          productAccess={productAccess}
          isPlanetAdmin={isPlanetAdmin}
        />
      )}

      <Stack data-testid="product-history-section" sx={{gap: 1, marginTop: 5}}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Product History
          </Typography>
          {ledgerAvailable && (
            <Stack direction="row" spacing={2} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={historyView}
                onChange={(e, v) => {
                  if (v) setHistoryView(v);
                }}
              >
                <ToggleButton
                  value="table"
                  sx={{textTransform: 'none', py: 0.25}}
                >
                  Table
                </ToggleButton>
                <ToggleButton
                  value="timeline"
                  sx={{textTransform: 'none', py: 0.25}}
                >
                  Timeline
                </ToggleButton>
              </ToggleButtonGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showAllTerms}
                    onChange={e => {
                      setShowAllTerms(e.target.checked);
                    }}
                  />
                }
                label={<Typography variant="body2">All terms</Typography>}
                labelPlacement="start"
                sx={{mr: 0}}
              />
            </Stack>
          )}
        </Stack>

        <Box sx={{display: 'flex', flexDirection: 'column', minHeight: 120}}>
          {ledgerAvailable && historyView === 'timeline' ? (
            <LedgerTimeline
              rows={groupedLedgerRows}
              customerAccountId={customerAccountId}
            />
          ) : ledgerAvailable ? (
            <>
              <DataGrid
                key={String(showAllTerms)}
                autoHeight
                columns={ledgerColumns}
                disableColumnFilter
                disableColumnSorting
                disableRowSelectionOnClick
                getRowClassName={params => {
                  if (!showAllTerms) return '';
                  const start = productAccess.term_start_date;
                  const end = productAccess.term_end_date;
                  if (!start || !end) return '';
                  // Dim rows that don't overlap the active term window, using the
                  // same half-open overlap the server applies in the scoped view.
                  const rFrom = params.row.valid_from ?? '';
                  const rTo = params.row.valid_to ?? '';
                  const overlaps =
                    (!rTo || rTo > start) && (!rFrom || rFrom < end);
                  return overlaps ? '' : 'ledger-other-term';
                }}
                getRowId={row =>
                  row.operation_id ??
                  `${row.operation_type}-${row.created_at}-${row.valid_from}`
                }
                sx={{
                  '& .MuiDataGrid-row': {cursor: 'default'},
                  '& .MuiDataGrid-row:hover': {bgcolor: 'transparent'},
                  '& .MuiDataGrid-row.ledger-other-term': {
                    bgcolor: 'grey.50',
                  },
                  '& .ledger-other-term:hover': {bgcolor: 'grey.100'},
                }}
                loading={ledgerLoading}
                rowCount={groupedLedgerRows.length}
                rows={groupedLedgerRows}
                slots={{
                  noRowsOverlay: () => (
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{p: 2}}
                    >
                      No quota history recorded. This product may predate the
                      ledger system.
                    </Typography>
                  ),
                }}
              />
              {groupedLedgerRows.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    pt: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Net unallocated
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {remainingUnallocated === null
                      ? 'Unlimited'
                      : renderQuota(
                          remainingUnallocated,
                          productAccess.quota_units ??
                            groupedLedgerRows.find(r => r.quota_units)
                              ?.quota_units
                        )}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <DataGrid
              autoHeight
              columns={columns}
              disableColumnFilter
              disableColumnSorting
              disableRowSelectionOnClick
              loading={isLoading}
              rowCount={(productAccess.line_items ?? []).length}
              rows={productAccess.line_items ?? []}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                noResultsOverlay: CustomNoRowsOverlay,
              }}
            />
          )}
        </Box>
      </Stack>

      {isRecallOpen && (
        <RecallWorkspacesModal
          open={isRecallOpen}
          onClose={() => setIsRecallOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['productAccess', productAccessId]);
            queryClient.invalidateQueries([
              'productAccessTerms',
              productAccessId,
            ]);
          }}
          workspacePas={workspacePas}
          workspacePasLoading={workspacePasLoading}
          workspacePasTruncated={workspacePasTruncated}
        />
      )}

      {isDeactivateModalOpen && (
        <DeactivateProductAccessModal
          handleModalClose={handleDeactivateModalClose}
          handleDeactivate={handleRemoveProduct}
          isLoading={isLoadingDeactivationResponse}
          productTitle={productTitle}
        />
      )}

      <Notifier
        open={isLoadingDeactivationResponse}
        message="Deactivating product..."
        progress
      />

      <Notifier
        open={isErrorDeactivating}
        message={errorDeactivation?.message}
        severity="error"
        autoHideDuration={5000}
      />
    </Box>
  );
};

export default NewProductDetailPage;
